/*
 * Studio v2 — keep the design across a reload, a back step or a visit to another page (sweep 2026-10-21 bug 3).
 * Before, the design lived only in the page's memory: 4 of the 20 visits that started a design lost it when the page
 * loaded again (phones reload a page when the shopper switches apps), and two of them left.
 *
 * What is kept: the order slots, the brief, the ideas, the group flow's step and the screens' memo (versions, the place
 * screen's size, the group form), and every file the design uses (the upload, the cleaned logo). Where: IndexedDB, one
 * record per browser tab (the tab's id is in sessionStorage, so another tab starts clean). A small marker in
 * sessionStorage says whether there is anything to restore, so a fresh visit never waits on IndexedDB.
 * Kept for 6 hours. Cleared when the order goes in the cart or the group is made, and by "Start over".
 *
 *   StudioKeep.peek()        → { at, style, gflow } or null (sync)
 *   StudioKeep.load()        → Promise<snapshot | null>
 *   StudioKeep.save(snap)    debounced; StudioKeep.flush() saves now (pagehide)
 *   StudioKeep.clear()
 */
(function () {
  const TAB = 'sv_keep_tab', META = 'sv_keep', DB = 'mv-studio', STORE = 'designs', TTL = 6 * 3600 * 1000;

  // Every blob: URL the page makes, with its Blob, so a saved design can carry the file itself (a blob: URL dies with
  // the page). This script loads before the Studio's, so it sees all of them.
  const blobs = new Map();
  const make = URL.createObjectURL.bind(URL);
  URL.createObjectURL = function (o) { const u = make(o); if (o instanceof Blob) blobs.set(u, o); return u; };

  const ss = {
    get(k) { try { return sessionStorage.getItem(k); } catch (_) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); return true; } catch (_) { return false; } },
    del(k) { try { sessionStorage.removeItem(k); } catch (_) {} },
  };
  function tab() {
    let t = ss.get(TAB);
    if (!t) { t = `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`; if (!ss.set(TAB, t)) return null; }
    return t;
  }

  let dbP = null;
  function db() {
    if (!dbP) {
      dbP = new Promise((res, rej) => {
        if (!window.indexedDB) return rej(new Error('no indexedDB'));
        const r = indexedDB.open(DB, 1);
        r.onupgradeneeded = () => r.result.createObjectStore(STORE);
        r.onsuccess = () => res(r.result);
        r.onerror = () => rej(r.error);
        r.onblocked = () => rej(new Error('indexedDB blocked'));
      });
      dbP.catch(() => { dbP = null; });
    }
    return dbP;
  }
  function req(mode, fn) {
    return db().then((d) => new Promise((res, rej) => {
      const t = d.transaction(STORE, mode), r = fn(t.objectStore(STORE));
      t.oncomplete = () => res(r ? r.result : undefined);
      t.onerror = t.onabort = () => rej(t.error);
    }));
  }

  // Plain data only. A blob: URL becomes { __blob: i } and a File or Blob { __file: i }, with the Blobs listed beside
  // (IndexedDB stores Blobs). Promises, DOM nodes, canvases and functions are dropped; so are cycles.
  function pack(root) {
    const files = [], ids = new Map(), path = new Set();
    const fileId = (b) => { if (!ids.has(b)) { ids.set(b, files.length); files.push(b); } return ids.get(b); };
    const walk = (v, depth) => {
      if (v == null || typeof v === 'number' || typeof v === 'boolean') return v;
      if (typeof v === 'string') {
        if (!v.startsWith('blob:')) return v;
        const b = blobs.get(v);
        return b ? { __blob: fileId(b) } : undefined;
      }
      if (typeof v !== 'object' || depth > 14 || path.has(v)) return undefined;
      if (v instanceof Blob) return { __file: fileId(v) };
      const proto = Object.getPrototypeOf(v);
      if (!Array.isArray(v) && proto !== Object.prototype && proto !== null) return undefined;
      path.add(v);
      let out;
      if (Array.isArray(v)) out = v.map((x) => { const y = walk(x, depth + 1); return y === undefined ? null : y; });
      else { out = {}; for (const k of Object.keys(v)) { const y = walk(v[k], depth + 1); if (y !== undefined) out[k] = y; } }
      path.delete(v);
      return out;
    };
    return { data: walk(root, 0), files };
  }
  function unpack(data, files) {
    const urls = new Map();
    const walk = (v) => {
      if (v == null || typeof v !== 'object') return v;
      if (Array.isArray(v)) return v.map(walk);
      if (typeof v.__blob === 'number' && Object.keys(v).length === 1) {
        const b = files[v.__blob];
        if (!b) return null;
        if (!urls.has(v.__blob)) urls.set(v.__blob, URL.createObjectURL(b));
        return urls.get(v.__blob);
      }
      if (typeof v.__file === 'number' && Object.keys(v).length === 1) return files[v.__file] || null;
      const out = {};
      for (const k of Object.keys(v)) out[k] = walk(v[k]);
      return out;
    };
    return walk(data);
  }

  function peek() {
    try {
      const m = JSON.parse(ss.get(META) || 'null');
      if (!m || Date.now() - m.at > TTL || m.tab !== tab()) return null;
      return m;
    } catch (_) { return null; }
  }
  function load() {
    const m = peek(), t = tab();
    if (!m || !t) return Promise.resolve(null);
    return req('readonly', (s) => s.get(t)).then((rec) => {
      if (!rec || Date.now() - rec.at > TTL) return null;
      return unpack(rec.data, rec.files || []);
    }).catch(() => null);
  }

  let pending = null, timer = 0, chain = Promise.resolve();
  function write() {
    timer = 0;
    const snap = pending; pending = null;
    const t = tab();
    if (!snap || !t) return chain;
    let rec;
    try { const p = pack(snap.data); rec = { at: Date.now(), data: p.data, files: p.files }; } catch (_) { return chain; }
    ss.set(META, JSON.stringify({ at: rec.at, tab: t, style: snap.style || null, gflow: !!snap.gflow }));
    chain = chain.then(() => req('readwrite', (s) => s.put(rec, t))).catch(() => {});
    return chain;
  }
  function save(snap) { pending = snap; if (!timer) timer = setTimeout(write, 400); }
  function flush() { if (timer) { clearTimeout(timer); return write(); } return chain; }
  function clear() {
    pending = null; if (timer) { clearTimeout(timer); timer = 0; }
    ss.del(META);
    const t = tab();
    if (t) chain = chain.then(() => req('readwrite', (s) => s.delete(t))).catch(() => {});
    return chain;
  }
  // The page is going away (a reload, another page, the app switcher): save what is pending now.
  addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });

  window.StudioKeep = { peek, load, save, flush, clear, pack, unpack };
})();
